// Self-registering "pick your variants" popup for a signature bundle.
// Reusable anywhere: any button on the page with
// data-bundle-favorite-trigger="<this element's id>" opens it (delegated
// listener below), and this file has no dependency on which page or
// section rendered the surrounding markup.

import { formatMoney } from '../modules/bundle-variant-popup';
import { trackEvent } from '../utils/analytics';
import { Dialog } from '../modules/dialog';
import type { URLBundleState } from '../modules/bundle-state';
import {
  esc,
  getDeviceDialogKind,
  isDeviceLikeOptionName,
  type DeviceDialogKind,
} from '../modules/bundle-variant-popup';

// One dialog per device kind, rendered once per section instance (see
// main-bundle-tool.liquid) and shared by every <bundle-favorite-popup> block
// on the page — same idea as bundle-tool.ts's own initDeviceDialogs(), just
// lazy and keyed by kind instead of eagerly wired in a constructor. Which
// kind opens is decided per-product via getDeviceDialogKind(product.productType)
// (see buildDeviceHelpButton) rather than always opening the phone dialog.
const DEVICE_HELP_CONTAINER_IDS: Record<DeviceDialogKind, string> = {
  phone: 'bundle-favorite-device-help-phone',
  clearCase: 'bundle-favorite-device-help-clear-case',
  kindle: 'bundle-favorite-device-help-kindle',
  skin: 'bundle-favorite-device-help-skin',
};

const deviceHelpDialogs = new WeakMap<HTMLDialogElement, Dialog>();

function getDeviceHelpDialog(kind: DeviceDialogKind): { dialog: Dialog; el: HTMLDialogElement } | null {
  const el = document.querySelector<HTMLDialogElement>(`#${DEVICE_HELP_CONTAINER_IDS[kind]} dialog`);
  if (!el) return null;
  let instance = deviceHelpDialogs.get(el);
  if (!instance) {
    const closeBtns = el.querySelectorAll<HTMLElement>('[data-dialog-toggle]');
    instance = new Dialog(el, [], closeBtns);
    deviceHelpDialogs.set(el, instance);
  }
  return { dialog: instance, el };
}

export interface FavoriteVariant {
  id: string;
  option1: string;
  option2: string;
  option3: string;
  price: number;
  compareAtPrice: number | null;
  available: boolean;
  image: string;
}

export interface FavoriteProductOption {
  name: string;
  values: string[];
}

export interface FavoriteProduct {
  id: string;
  handle: string;
  productType: string;
  title: string;
  image: string;
  options: FavoriteProductOption[];
  variants: FavoriteVariant[];
}

export interface FavoriteBundleData {
  name: string;
  discountPercent: number;
  products: FavoriteProduct[];
}

interface StepState {
  product: FavoriteProduct;
  selectedValues: Record<string, string>;
  resolvedVariant: FavoriteVariant | null;
}

class BundleFavoritePopup extends HTMLElement {
  private dialog!: HTMLDialogElement;
  private imagesEl!: HTMLElement;
  private controlsEl!: HTMLElement;
  private backBtn!: HTMLButtonElement;
  private nextBtn!: HTMLButtonElement;
  private closeBtn!: HTMLButtonElement;
  private data!: FavoriteBundleData;
  private steps: StepState[] = [];
  private currentStep = 0;

  connectedCallback() {
    this.dialog = this.querySelector('.bundle-favorite-popup__dialog') as HTMLDialogElement;
    this.imagesEl = this.querySelector('.bundle-favorite-popup__images') as HTMLElement;
    this.controlsEl = this.querySelector('.bundle-favorite-popup__controls') as HTMLElement;
    this.backBtn = this.querySelector('.bundle-favorite-popup__back') as HTMLButtonElement;
    this.nextBtn = this.querySelector('.bundle-favorite-popup__next') as HTMLButtonElement;
    this.closeBtn = this.querySelector('.bundle-favorite-popup__close') as HTMLButtonElement;

    const dataScript = this.querySelector('.bundle-favorite-popup__data');
    this.data = JSON.parse(dataScript?.textContent ?? '{"name":"","discountPercent":0,"products":[]}');
    // A product with every variant sold out can never resolve a variant (see
    // updateResolvedVariant), which used to permanently disable Next on its step —
    // the wizard had no way to skip it, so the whole signature bundle became
    // unaddable. Drop it from the wizard entirely; the rest of the bundle still adds.
    this.steps = this.data.products
      .filter((product) => product.variants.some((v) => v.available))
      .map((product) => ({
        product,
        selectedValues: {},
        resolvedVariant: null,
      }));

    this.closeBtn.addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });
    this.backBtn.addEventListener('click', () => this.goToStep(this.currentStep - 1));
    this.nextBtn.addEventListener('click', () => this.handleNextOrAddToCart());
  }

  open() {
    // An unconfigured signature-bundle block (no products assigned in the
    // admin) leaves `steps` empty. Nothing downstream (renderStep,
    // renderControls) can render a step in that case, so bail before
    // showModal() rather than opening a dialog we can't populate — the
    // Liquid side already hides the trigger for this state (Task 6), this
    // is just the JS-level backstop.
    if (this.steps.length === 0) return;

    this.currentStep = 0;
    this.steps.forEach((s) => {
      s.selectedValues = {};
      s.resolvedVariant = null;
    });
    this.dialog.classList.remove('is-closing');
    this.dialog.showModal();
    document.body.classList.add('--no-scroll');
    this.renderStep();
    trackEvent('bundle_signature_open', {
      bundle_name: this.data.name,
      discount_percent: this.data.discountPercent,
      product_count: this.steps.length,
    });
  }

  close() {
    // Tied to the CSS animation actually finishing (via `animationend`)
    // rather than a hardcoded timeout — necessary now that
    // .faq-content-dialog's close animation is a slide (mobile) or slide
    // (desktop) via a @media query, not a simple fade.
    this.dialog.classList.add('is-closing');
    this.dialog.addEventListener(
      'animationend',
      () => {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        document.body.classList.remove('--no-scroll');
      },
      { once: true }
    );
  }

  private goToStep(index: number) {
    if (index < 0 || index >= this.steps.length) return;
    this.currentStep = index;
    this.renderStep();
  }

  private renderStep() {
    this.renderImages();
    this.backBtn.hidden = this.currentStep === 0;
    const isFr = document.documentElement.lang?.startsWith('fr') ?? false;
    // The last step hands off to the bundle builder (handleNextOrAddToCart),
    // it never adds to cart itself, so the label must not promise a cart add.
    this.nextBtn.textContent =
      this.currentStep === this.steps.length - 1
        ? (isFr ? 'Continuer vers mon ensemble' : 'Continue to my bundle')
        : isFr ? 'Suivant' : 'Next';
    this.renderControls();
  }

  // Renders one control per real option on the current step's product,
  // skipping any option with a single value (nothing to choose — this is
  // what makes a true single-variant product's step show no controls at
  // all, per the design: still a step, just image + Next).
  private renderControls() {
    const step = this.steps[this.currentStep];
    this.controlsEl.innerHTML = '';
    const isFr = document.documentElement.lang?.startsWith('fr') ?? false;

    step.product.options.forEach((option) => {
      if (option.values.length <= 1) return;

      const isDeviceLike = isDeviceLikeOptionName(option.name);
      const wrapper = document.createElement('div');
      wrapper.className = 'bundle-favorite-popup__option';

      const label = document.createElement('div');
      label.className = 'bundle-favorite-popup__option-label';
      label.textContent = this.optionLabel(option.name, step, isFr);
      wrapper.appendChild(label);

      if (isDeviceLike) {
        wrapper.appendChild(this.buildDeviceControl(option, step));
        const kind = getDeviceDialogKind(step.product.productType);
        if (kind) wrapper.appendChild(this.buildDeviceHelpButton(isFr, kind, step));
      } else {
        wrapper.appendChild(this.buildPillControl(option, step));
      }

      this.controlsEl.appendChild(wrapper);
    });

    this.updateResolvedVariant();
  }

  // MagSafe Ring is relabeled "Pixel Snap Ring" for Pixel devices, same
  // convention as variant-phone-case.liquid's server-rendered label. Uses
  // `includes` (not `startsWith`) because real Device values are like
  // "Google Pixel 10", not "Pixel 10" — matches the Liquid snippet's
  // `product.selected_or_first_available_variant.title contains 'Pixel'`
  // check, which is also a substring match, not a prefix match.
  private optionLabel(optionName: string, step: StepState, isFr: boolean): string {
    if (optionName.toLowerCase().includes('magsafe') || optionName.toLowerCase().includes('snap')) {
      const device = this.deviceValueFor(step);
      const isPixel = (device ?? '').toLowerCase().includes('pixel');
      if (isPixel) return isFr ? 'Anneau Pixel Snap' : 'Pixel Snap Ring';
      return isFr ? 'Anneau MagSafe' : 'MagSafe Ring';
    }
    return optionName;
  }

  private deviceValueFor(step: StepState): string | undefined {
    const deviceOption = step.product.options.find((o) => isDeviceLikeOptionName(o.name));
    return deviceOption ? step.selectedValues[deviceOption.name] : undefined;
  }

  private buildDeviceControl(option: FavoriteProductOption, step: StepState): HTMLSelectElement {
    const select = document.createElement('select');
    select.className = 'bundle-favorite-popup__device-select';
    option.values.forEach((value) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = value;
      select.appendChild(opt);
    });
    const current = step.selectedValues[option.name] ?? option.values[0];
    select.value = current;
    step.selectedValues[option.name] = current;
    select.addEventListener('change', () => {
      step.selectedValues[option.name] = select.value;
      this.renderControls();
      this.renderImages();
    });
    return select;
  }

  private buildDeviceHelpButton(isFr: boolean, kind: DeviceDialogKind, step: StepState): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bundle-favorite-popup__device-help';
    btn.textContent = isFr ? 'Pas certain de votre appareil?' : 'Not sure which device you have?';
    btn.addEventListener('click', () => {
      const entry = getDeviceHelpDialog(kind);
      if (!entry) return;

      if (kind === 'phone') {
        // Mirrors BundlePopup.openDeviceHelp('phone') in bundle-tool.ts — Folio
        // Case doesn't come in Pixel sizes, and the tab needs resetting since
        // this same dialog is reused across every product in the wizard.
        const isFolio = step.product.productType === 'Folio Case';
        entry.el.querySelectorAll<HTMLElement>('[data-target="pixel"]').forEach((tab) => { tab.hidden = isFolio; });
        const tabButtons = entry.el.querySelectorAll<HTMLButtonElement>('[data-target]');
        const groups = entry.el.querySelectorAll<HTMLElement>('[data-dialog-group]');
        tabButtons.forEach((tab) => tab.classList.toggle('active', tab.dataset.target === 'iphone'));
        groups.forEach((group) => group.classList.toggle('active', group.dataset.dialogGroup === 'iphone'));
      } else if (kind === 'kindle' || kind === 'skin') {
        this.populateModelList(entry, step);
      }

      // entry.dialog is a shared Dialog instance (device-help dialogs are
      // reused across every product in the wizard) whose own close()
      // unconditionally clears --no-scroll — correct when it's the only
      // thing open, wrong here since it's nested inside this still-open
      // popup. Re-lock right after its close() finishes removing the class.
      entry.el.addEventListener('close', () => {
        if (this.dialog.open) {
          setTimeout(() => document.body.classList.add('--no-scroll'), 0);
        }
      }, { once: true });

      entry.dialog.open();
    });
    return btn;
  }

  // Kindle/Skin dialogs have no static model list to render (see
  // dialog-kindle-device.liquid / dialog-skin-device.liquid — their
  // `for variant in product.variants` loop has no `product` in scope on this
  // page), so it's populated here from the step's already-fetched variants,
  // same idea as VariantPopupRenderer.populateModelList in
  // bundle-variant-popup.ts but against FavoriteVariant's leaner shape (no
  // `.title` — these products are single-option, so option1 doubles as the
  // display name).
  private populateModelList(entry: { dialog: Dialog; el: HTMLDialogElement }, step: StepState) {
    const list = entry.el.querySelector<HTMLUListElement>('[data-model-list]');
    const searchInput = entry.el.querySelector<HTMLInputElement>('[data-model-search]');
    if (!list) return;

    const isFr = document.documentElement.lang?.startsWith('fr') ?? false;
    const chooseLabel = isFr ? 'Choisir' : 'Choose';
    const soldOutLabel = isFr ? 'Épuisé' : 'Sold out';
    const deviceOption = step.product.options.find((o) => isDeviceLikeOptionName(o.name));
    const deviceOptionName = deviceOption?.name ?? '';

    list.innerHTML = step.product.variants.map((v) => `
      <li data-model-item>
        <div><strong>${esc(v.option1 ?? '')}</strong></div>
        ${v.available
          ? `<button type="button" data-select-variant="${esc(v.id)}" data-variant-value="${esc(v.option1 ?? '')}" class="button-v2">${esc(chooseLabel)}</button>`
          : `<button type="button" disabled>${esc(soldOutLabel)}</button>`}
      </li>
    `).join('');

    if (searchInput) searchInput.value = '';

    list.querySelectorAll<HTMLButtonElement>('[data-select-variant]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.variantValue;
        if (!value || !deviceOptionName) return;
        step.selectedValues[deviceOptionName] = value;
        this.renderControls();
        this.renderImages();
        entry.dialog.close();
      });
    });
  }

  private buildPillControl(option: FavoriteProductOption, step: StepState): HTMLDivElement {
    const group = document.createElement('div');
    group.className = 'bundle-favorite-popup__pill-group';
    const isMagsafe = option.name.toLowerCase().includes('magsafe') || option.name.toLowerCase().includes('snap');

    option.values.forEach((value) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-favorite-popup__pill button-toggle';
      btn.textContent = isMagsafe ? this.magsafePillLabel(value, option, step) : value;

      const current = step.selectedValues[option.name] ?? option.values[0];
      step.selectedValues[option.name] = current;
      btn.classList.toggle('active', current === value);

      btn.addEventListener('click', () => {
        step.selectedValues[option.name] = value;
        this.renderControls();
      });
      group.appendChild(btn);
    });

    return group;
  }

  // "+$X.00" price delta between this option's value and the sibling
  // "Without"-equivalent variant at the currently selected device, same
  // computation as variant-phone-case.liquid's magsafe_price_diff.
  private magsafePillLabel(value: string, option: FavoriteProductOption, step: StepState): string {
    if (value.toLowerCase() !== 'with') return value;
    const deviceOption = step.product.options.find((o) => isDeviceLikeOptionName(o.name));
    const device = deviceOption ? step.selectedValues[deviceOption.name] : undefined;
    if (!deviceOption || !device) return value;

    const deviceIdx = step.product.options.indexOf(deviceOption);
    const magsafeIdx = step.product.options.indexOf(option);
    const deviceKey = `option${deviceIdx + 1}` as 'option1' | 'option2' | 'option3';
    const magsafeKey = `option${magsafeIdx + 1}` as 'option1' | 'option2' | 'option3';

    const withVariant = step.product.variants.find(
      (v) => v[deviceKey] === device && v[magsafeKey] === 'With' && v.available
    );
    const withoutVariant = step.product.variants.find(
      (v) => v[deviceKey] === device && v[magsafeKey] === 'Without' && v.available
    );
    if (!withVariant || !withoutVariant) return value;

    const diff = withVariant.price - withoutVariant.price;
    if (diff <= 0) return value;
    // FR/EN-aware formatting matching the cart's own price convention (see
    // cart-bundle-group.ts, bundle-tool.ts) — avoids a hardcoded "$"/en-style
    // decimal that would be wrong on FR-locale storefronts.
    const isFr = document.documentElement.lang?.startsWith('fr') ?? false;
    return `${value} +${formatMoney(diff, isFr ? 'fr' : 'en')}`;
  }

  // Matches the current step's selectedValues against the product's real
  // variants (same option1/2/3 comparison approach as bundle-tool.ts's
  // resolveSeedVariantId, generalized to every option instead of just
  // device). Enables Next/Add to cart only once a variant is found.
  private updateResolvedVariant() {
    const step = this.steps[this.currentStep];
    const match = step.product.variants.find((variant) => {
      if (!variant.available) return false;
      return step.product.options.every((opt, idx) => {
        const key = `option${idx + 1}` as 'option1' | 'option2' | 'option3';
        const wanted = step.selectedValues[opt.name];
        return wanted === undefined || variant[key] === wanted;
      });
    });
    step.resolvedVariant = match ?? null;
    this.nextBtn.disabled = !step.resolvedVariant;
    this.renderImages();
  }

  private renderImages() {
    this.imagesEl.innerHTML = '';
    const step = this.steps[this.currentStep];
    if (!step) return;

    const img = document.createElement('img');
    img.src = step.resolvedVariant?.image || step.product.image;
    img.alt = step.product.title;
    img.className = 'bundle-favorite-popup__images-image--active';
    this.imagesEl.appendChild(img);
  }

  // Resolves each step's chosen variant into a {handle, variantId} ref — the builder
  // (bundle-tool.ts's BundlePage.loadFromURL/rehydrateProducts) refetches the rest
  // (price, title, image, productType) by handle on landing, so nothing else needs
  // to travel in the URL. typeName is the one piece of curated content with no
  // Shopify source, and does travel — it seeds BundlePage.incomingTypeName.
  private buildBundleState(): URLBundleState {
    return {
      typeName: this.data.name,
      products: this.steps.map((s) => ({
        handle: s.product.handle,
        variantId: String(s.resolvedVariant!.id),
      })),
    };
  }

  private async handleNextOrAddToCart() {
    if (this.currentStep < this.steps.length - 1) {
      this.goToStep(this.currentStep + 1);
      return;
    }

    const resolved = this.steps.every((s) => s.resolvedVariant !== null);
    if (!resolved) return; // guarded by disabled Next, but don't hand off a partial bundle

    const guard = window.kaseMeBundleReplaceGuard;
    if (guard?.hasUnsaved()) {
      const decision = await guard.confirm();
      if (decision === 'cancel') return;
    }

    trackEvent('bundle_signature_handoff', {
      bundle_name: this.data.name,
      discount_percent: this.data.discountPercent,
      product_count: this.steps.length,
    });
    const params = new URLSearchParams();
    params.set('bundle', JSON.stringify(this.buildBundleState()));
    window.location.href = `${Shopify.routes.root}pages/bundle?${params.toString()}`;
  }
}

if (!customElements.get('bundle-favorite-popup')) {
  customElements.define('bundle-favorite-popup', BundleFavoritePopup);
}

// Delegated so any future page can add its own trigger button without this
// file needing to know about it — matches the reusability requirement.
document.addEventListener('click', (e) => {
  const trigger = (e.target as HTMLElement)?.closest('[data-bundle-favorite-trigger]');
  if (!trigger) return;
  const popupId = trigger.getAttribute('data-bundle-favorite-trigger');
  if (!popupId) return;
  const popup = document.getElementById(popupId) as BundleFavoritePopup | null;
  popup?.open();
});
